import { api } from './client';

// Types for the unified filter options response
export interface FilterOptionItem {
  [key: string]: string;
}

export interface SiteOption {
  site_id: string;
  site_name: string;
}

export interface ModelOption {
  model_id: string;
  model_name: string;
}

export interface MajorComponentOption {
  major_component_id: string;
  major_component_name: string;
}

export interface ComponentOption {
  component_id: string;
  component_name: string;
}

export interface OutcomeOption {
  outcome_id: string;
  outcome_name: string;
}

export interface FailureModeOption {
  failure_mode_name: string;
}

// Backend response structure (from /api/v1/filter_params)
export interface BackendFilterParamsResponse {
  sites: SiteOption[];
  models: ModelOption[];
  major_components: MajorComponentOption[];
  components: ComponentOption[];
  outcomes: OutcomeOption[];
  failure_modes: FailureModeOption[];
  major_corrective: string[];
  planned_reactive: string[];
}

// Frontend expected structure (for backwards compatibility)
export interface UnifiedFilterOptionsResponse {
  sites: SiteOption[];
  models: ModelOption[];
  major_components: MajorComponentOption[];
  components: ComponentOption[];
  outcomes: OutcomeOption[];
  failure_mode_name: FailureModeOption[];
  major_corrective: string[];
  planned_reactive: string[];
}

export interface FilterOptions {
  sites: string[];
  turbineModels: string[];
  componentsMajor: string[];
  componentsMinor: string[];
  failureModes: string[];
  outcomeName: string[];
  excludedCaseIds: string[];
  majorCorrective: string[];
  plannedReactive: string[];
}

export interface FilterMappings {
  sites: Record<string, string>;
  models: Record<string, string>;
  majorComponents: Record<string, string>;
  components: Record<string, string>;
  outcomeName: Record<string, string>;
}

/**
 * Service for fetching filter options efficiently
 */
class FilterOptionsService {
  private cache: FilterOptions | null = null;
  private mappingsCache: FilterMappings | null = null;
  private cacheTimestamp = 0;
  private readonly CACHE_DURATION = 10 * 60 * 1000; // 10 minutes
  private isLoading = false;

  /**
   * Get filter options from the unified endpoint
   */
  async getFilterOptions(): Promise<FilterOptions> {
    const now = Date.now();

    // Return cached data if still fresh
    if (this.cache && now - this.cacheTimestamp < this.CACHE_DURATION) {
      return this.cache;
    }

    // Prevent multiple simultaneous requests
    if (this.isLoading) {
      while (this.isLoading) {
        await new Promise((resolve) => setTimeout(resolve, 100));
      }
      return this.cache || this.getEmptyOptions();
    }

    this.isLoading = true;

    try {
      const response = await api.get<BackendFilterParamsResponse>('/filter_params');

      // Convert backend response to frontend format
      const frontendResponse = this.convertBackendResponse(response);

      // Parse the response and create both display options and mappings
      const { filterOptions, mappings } = this.parseUnifiedResponse(frontendResponse);

      this.cache = filterOptions;
      this.mappingsCache = mappings;
      this.cacheTimestamp = now;

      return this.cache;
    } catch (error) {
      console.error('Failed to fetch filter options:', error);
      this.cache = this.getEmptyOptions();
      this.cacheTimestamp = now;
      return this.cache;
    } finally {
      this.isLoading = false;
    }
  }

  /**
   * Get filter mappings (name to UUID)
   */
  async getFilterMappings(): Promise<FilterMappings> {
    // Ensure we have the mappings by fetching filter options
    if (!this.mappingsCache) {
      await this.getFilterOptions();
    }

    return this.mappingsCache || this.getEmptyMappings();
  }

  /**
   * Parse the unified response into display options and mappings
   */
  private parseUnifiedResponse(response: UnifiedFilterOptionsResponse): {
    filterOptions: FilterOptions;
    mappings: FilterMappings;
  } {
    // Extract display names
    const sites = response.sites.map((s) => s.site_name).sort();
    const turbineModels = response.models.map((m) => m.model_name).sort();
    const componentsMajor = response.major_components.map((c) => c.major_component_name).sort();
    const componentsMinor = response.components.map((c) => c.component_name).sort();
    const failureModes = response.failure_mode_name.map((f) => f.failure_mode_name).sort();
    const outcomeName = response.outcomes.map((o) => o.outcome_name).sort();
    const majorCorrective = response.major_corrective;
    const plannedReactive = response.planned_reactive;

    // Create mappings (name -> UUID)
    const siteMappings: Record<string, string> = {};
    response.sites.forEach((s) => {
      siteMappings[s.site_name] = s.site_id;
    });

    const modelMappings: Record<string, string> = {};
    response.models.forEach((m) => {
      modelMappings[m.model_name] = m.model_id;
    });

    const majorComponentMappings: Record<string, string> = {};
    response.major_components.forEach((c) => {
      majorComponentMappings[c.major_component_name] = c.major_component_id;
    });

    const componentMappings: Record<string, string> = {};
    response.components.forEach((c) => {
      componentMappings[c.component_name] = c.component_id;
    });

    const outcomeMappings: Record<string, string> = {};
    response.outcomes.forEach((o) => {
      outcomeMappings[o.outcome_name] = o.outcome_id;
    });

    const filterOptions: FilterOptions = {
      sites,
      turbineModels,
      componentsMajor,
      componentsMinor,
      failureModes,
      outcomeName,
      majorCorrective,
      plannedReactive,
      excludedCaseIds: [],
    };

    const mappings: FilterMappings = {
      sites: siteMappings,
      models: modelMappings,
      majorComponents: majorComponentMappings,
      components: componentMappings,
      outcomeName: outcomeMappings,
    };

    return { filterOptions, mappings };
  }

  /**
   * Get specific filter options (for performance)
   */
  async getSitesOnly(): Promise<string[]> {
    const options = await this.getFilterOptions();
    return options.sites;
  }

  async getComponentsOnly(): Promise<{ major: string[]; minor: string[] }> {
    const options = await this.getFilterOptions();
    return {
      major: options.componentsMajor,
      minor: options.componentsMinor,
    };
  }

  /**
   * Clear cache manually (useful for data updates)
   */
  clearCache(): void {
    this.cache = null;
    this.mappingsCache = null;
    this.cacheTimestamp = 0;
  }

  /**
   * Get cache info for debugging
   */
  getCacheInfo(): { cached: boolean; age: number; size: number } {
    const now = Date.now();
    return {
      cached: !!this.cache,
      age: this.cache ? now - this.cacheTimestamp : 0,
      size: this.cache ? Object.values(this.cache).flat().length : 0,
    };
  }

  private getEmptyOptions(): FilterOptions {
    return {
      sites: [],
      turbineModels: [],
      componentsMajor: [],
      componentsMinor: [],
      failureModes: [],
      outcomeName: [],
      majorCorrective: [],
      plannedReactive: [],
      excludedCaseIds: [],
    };
  }

  private getEmptyMappings(): FilterMappings {
    return {
      sites: {},
      models: {},
      majorComponents: {},
      components: {},
      outcomeName: {},
    };
  }

  /**
   * Convert backend response format to frontend format
   */
  private convertBackendResponse(
    backendResponse: BackendFilterParamsResponse
  ): UnifiedFilterOptionsResponse {
    return {
      sites: backendResponse.sites,
      models: backendResponse.models,
      major_components: backendResponse.major_components,
      components: backendResponse.components,
      outcomes: backendResponse.outcomes,
      failure_mode_name: backendResponse.failure_modes, // Map failure_modes to failure_mode_name
      major_corrective: backendResponse.major_corrective,
      planned_reactive: backendResponse.planned_reactive
    };
  }

}

// Export singleton instance
export const filterOptionsService = new FilterOptionsService();
